import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Phone, MapPin, Clock, Facebook, 
  Send, Loader2, CheckCircle, Shield, User, Building2
} from "lucide-react";
import axios from "axios";
import { useSiteConfig } from "@/context/SiteConfigContext";
import SEOHead from "@/components/SEOHead";
import { contactPointSchema, organizationSchema } from "@/utils/schemas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultCMS = {
  hero: {
    headline1: "Contact",
    headline2: "Overhaul Ops",
    description: "Ready to get started? Fill out the form below or give us a call. We respond to all inquiries within 24 hours."
  }
};

// Skeleton loader component for Contact page
const ContactSkeleton = () => (
  <div data-testid="contact-page" className="pt-20">
    <section className="relative py-20 md:py-32 bg-[#1a1a1a] noise-texture">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl animate-pulse">
          <div className="h-4 w-24 bg-white/20 rounded mb-4"></div>
          <div className="h-16 w-80 bg-white/20 rounded mb-6"></div>
          <div className="h-6 w-96 bg-white/20 rounded"></div>
        </div>
      </div>
    </section>
    <section className="py-20 md:py-32 bg-[#f4f4f5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 animate-pulse">
          <div className="lg:col-span-2">
            <div className="bg-white p-8 md:p-12 rounded-sm shadow-sm">
              <div className="h-8 w-48 bg-gray-200 rounded mb-8"></div>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="h-12 bg-gray-200 rounded"></div>
                  <div className="h-12 bg-gray-200 rounded"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="h-12 bg-gray-200 rounded"></div>
                  <div className="h-12 bg-gray-200 rounded"></div>
                </div>
                <div className="h-32 bg-gray-200 rounded"></div>
                <div className="h-12 w-40 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
          <div className="space-y-8">
            <div className="bg-white p-8 rounded-sm shadow-sm h-48"></div>
            <div className="bg-white p-8 rounded-sm shadow-sm h-64"></div>
          </div>
        </div>
      </div>
    </section>
  </div>
);

const services = [
  "Junk Removal",
  "Material Hauling",
  "Clean Out",
  "Light Demolition",
  "Commercial Services",
  "Other"
];

const contactMethods = [
  "Phone Call",
  "Text Message",
  "Email"
];

const hearAboutUs = [
  "Google Search",
  "Facebook",
  "Friend/Family Referral",
  "Saw Our Truck",
  "Nextdoor",
  "Yelp",
  "Other"
];

const businessHours = [
  { day: "Monday", hours: "7AM - 6PM" },
  { day: "Tuesday", hours: "7AM - 6PM" },
  { day: "Wednesday", hours: "7AM - 6PM" },
  { day: "Thursday", hours: "7AM - 6PM" },
  { day: "Friday", hours: "7AM - 6PM" },
  { day: "Saturday", hours: "7AM - 6PM" },
  { day: "Sunday", hours: "7AM - 6PM" },
];

const serviceAreas = [
  { name: "Denton County", slug: "denton-denton-junk-removal" },
  { name: "Tarrant County", slug: "fort-worth-tarrant-junk-removal" },
  { name: "Parker County", slug: "aledo-parker-junk-removal" },
  { name: "Wise County", slug: "decatur-wise-junk-removal" }
];

// Interactive map regions with approximate positions for DFW counties
const mapRegions = [
  { 
    name: "Wise County", 
    slug: "decatur-wise-junk-removal",
    cities: ["Decatur", "Bridgeport", "Aurora"],
    position: { top: "8%", left: "5%", width: "35%", height: "35%" },
    color: "#b51a00"
  },
  { 
    name: "Denton County", 
    slug: "denton-denton-junk-removal",
    cities: ["Denton", "Lewisville", "Flower Mound", "Frisco"],
    position: { top: "8%", left: "40%", width: "35%", height: "40%" },
    color: "#d63a1e"
  },
  { 
    name: "Parker County", 
    slug: "aledo-parker-junk-removal",
    cities: ["Weatherford", "Aledo", "Willow Park"],
    position: { top: "50%", left: "5%", width: "30%", height: "42%" },
    color: "#8a1300"
  },
  { 
    name: "Tarrant County", 
    slug: "fort-worth-tarrant-junk-removal",
    cities: ["Fort Worth", "Arlington", "Keller", "Southlake"],
    position: { top: "48%", left: "35%", width: "40%", height: "45%" },
    color: "#c92a10"
  }
];

export default function Contact() {
  const { formatted_phone, phone_link, google_maps_api_key, google_business_place_id } = useSiteConfig();
  const [contactType, setContactType] = useState("client");
  const [cms, setCms] = useState(defaultCMS);
  const [cmsLoaded, setCmsLoaded] = useState(false);
  const [formData, setFormData] = useState({
    // Common fields
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    message: "",
    sms_consent: false,
    // Client-specific
    service: "",
    contact_method: "",
    hear_about_us: "",
    // Vendor-specific
    business_name: "",
    position: "",
    // Honeypot (hidden field for bots)
    website: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchCMS = async () => {
      try {
        const response = await axios.get(`${API}/cms/page/contact`);
        if (response.data?.length > 0) {
          const content = { ...defaultCMS };
          response.data.forEach(item => {
            if (item.section && item.content) {
              content[item.section] = { ...content[item.section], ...item.content };
            }
          });
          setCms(content);
        }
      } catch (e) { /* use defaults */ }
      finally {
        setCmsLoaded(true);
      }
    };
    fetchCMS();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot check - if filled, it's a bot
    if (formData.website) {
      // Silently "succeed" but don't actually submit
      setSubmitted(true);
      return;
    }

    // Validation
    if (!formData.name || !formData.phone || !formData.email) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (contactType === "vendor" && (!formData.business_name || !formData.position || !formData.address)) {
      toast.error("Please fill in all required vendor fields");
      return;
    }

    if (contactType === "client" && !formData.service) {
      toast.error("Please select a service");
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        ...formData,
        contact_type: contactType,
        service: contactType === "client" ? formData.service : "Vendor Inquiry"
      };
      // Remove honeypot field before sending
      delete submitData.website;
      
      await axios.post(`${API}/contact`, submitData);
      setSubmitted(true);
      toast.success("Your message has been sent! We'll be in touch soon.");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  // Show skeleton while CMS loads
  if (!cmsLoaded) {
    return <ContactSkeleton />;
  }

  if (submitted) {
    return (
      <div data-testid="contact-success" className="pt-20 min-h-screen bg-[#f4f4f5] flex items-center justify-center">
        <div className="max-w-lg mx-auto text-center px-4">
          <div className="bg-white p-12 rounded-sm shadow-lg">
            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
            <h2 
              className="text-3xl font-bold uppercase tracking-tight mb-4 text-[#0f0f0f]"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Message Sent!
            </h2>
            <p className="text-[#4a4a4a] mb-6">
              Thank you for reaching out. We'll review your request and get back to you shortly.
            </p>
            <div className="bg-[#f4f4f5] p-4 rounded-sm mb-6">
              <p className="text-sm text-[#707070]">Need immediate assistance?</p>
              <a 
                href={phone_link} 
                className="text-[#b51a00] font-bold text-lg hover:underline"
              >
                {formatted_phone}
              </a>
            </div>
            <Button 
              onClick={() => {
                setSubmitted(false);
                setFormData({
                  name: "", email: "", phone: "", address: "", city: "", message: "",
                  sms_consent: false, service: "", contact_method: "", hear_about_us: "",
                  business_name: "", position: "", website: ""
                });
              }}
              className="bg-[#b51a00] hover:bg-[#8a1300] text-white font-bold uppercase tracking-wider rounded-sm px-6"
            >
              Send Another Message
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page" className="pt-20">
      <SEOHead
        title="Contact Us - Get a Free Junk Removal Quote"
        description="Contact Overhaul Ops for fast, professional junk removal in DFW. Call (817) 682-1555 or fill out our form for a free, no-obligation quote."
        keywords="contact junk removal DFW, free junk removal quote, junk removal phone number, schedule junk removal"
        schemas={[contactPointSchema(), organizationSchema()]}
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Contact", url: "/contact" }]}
      />
      <section className="relative py-20 md:py-32 bg-[#1a1a1a] noise-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#b51a00] font-bold uppercase tracking-widest text-sm">
              Get in Touch
            </span>
            <h1 
              className="text-5xl md:text-7xl font-black text-white uppercase tracking-wide leading-none mt-2 mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {cms.hero.headline1}<br />
              <span className="text-[#b51a00]">{cms.hero.headline2}</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              {cms.hero.description}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 md:py-32 bg-[#f4f4f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white p-8 md:p-12 rounded-sm shadow-sm">
                <h2 
                  className="text-3xl font-bold uppercase tracking-tight mb-8 text-[#0f0f0f]"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  Request a Free Quote
                </h2>
                
                <form onSubmit={handleSubmit} className="contact-form space-y-6">
                  {/* Honeypot field - hidden from users, visible to bots */}
                  <div className="hidden" aria-hidden="true">
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      name="website"
                      type="text"
                      value={formData.website}
                      onChange={handleChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  {/* Contact Type Selection */}
                  <div className="mb-8">
                    <Label className="text-sm font-bold uppercase tracking-wider text-[#4a4a4a] mb-4 block">
                      I am a: *
                    </Label>
                    <RadioGroup 
                      value={contactType} 
                      onValueChange={setContactType}
                      className="flex flex-col sm:flex-row gap-4"
                    >
                      <div className={`flex items-center gap-3 p-4 border-2 rounded-sm cursor-pointer transition-all ${contactType === 'client' ? 'border-[#b51a00] bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <RadioGroupItem value="client" id="client" />
                        <Label htmlFor="client" className="cursor-pointer flex items-center gap-2">
                          <User className="w-5 h-5 text-[#b51a00]" />
                          <span className="font-medium">Client</span>
                          <span className="text-sm text-gray-500">(Need junk removal)</span>
                        </Label>
                      </div>
                      <div className={`flex items-center gap-3 p-4 border-2 rounded-sm cursor-pointer transition-all ${contactType === 'vendor' ? 'border-[#b51a00] bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <RadioGroupItem value="vendor" id="vendor" />
                        <Label htmlFor="vendor" className="cursor-pointer flex items-center gap-2">
                          <Building2 className="w-5 h-5 text-[#b51a00]" />
                          <span className="font-medium">Vendor</span>
                          <span className="text-sm text-gray-500">(Business inquiry)</span>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Vendor-specific Fields */}
                  {contactType === "vendor" && (
                    <div className="p-6 bg-gray-50 rounded-sm space-y-4 mb-6">
                      <h3 className="font-bold text-[#0f0f0f] uppercase tracking-wider text-sm">Business Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="business_name" className="text-sm font-bold uppercase tracking-wider text-[#4a4a4a]">
                            Business Name *
                          </Label>
                          <Input
                            id="business_name"
                            name="business_name"
                            data-testid="vendor-business-name"
                            value={formData.business_name}
                            onChange={handleChange}
                            placeholder="Your company name"
                            className="mt-2 rounded-sm border-2 focus:border-[#b51a00]"
                            required={contactType === "vendor"}
                          />
                        </div>
                        <div>
                          <Label htmlFor="position" className="text-sm font-bold uppercase tracking-wider text-[#4a4a4a]">
                            Your Position *
                          </Label>
                          <Input
                            id="position"
                            name="position"
                            data-testid="vendor-position"
                            value={formData.position}
                            onChange={handleChange}
                            placeholder="e.g., Owner, Manager"
                            className="mt-2 rounded-sm border-2 focus:border-[#b51a00]"
                            required={contactType === "vendor"}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Common Fields - Name, Phone, Email */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-sm font-bold uppercase tracking-wider text-[#4a4a4a]">
                        {contactType === "vendor" ? "Contact Name *" : "Name *"}
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="contact-name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className="mt-2 rounded-sm border-2 focus:border-[#b51a00]"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-sm font-bold uppercase tracking-wider text-[#4a4a4a]">
                        Phone *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        data-testid="contact-phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(123) 456-7890"
                        className="mt-2 rounded-sm border-2 focus:border-[#b51a00]"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email" className="text-sm font-bold uppercase tracking-wider text-[#4a4a4a]">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="contact-email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="mt-2 rounded-sm border-2 focus:border-[#b51a00]"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="address" className="text-sm font-bold uppercase tracking-wider text-[#4a4a4a]">
                        Address {contactType === "vendor" ? "*" : ""}
                      </Label>
                      <Input
                        id="address"
                        name="address"
                        data-testid="contact-address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="123 Main St"
                        className="mt-2 rounded-sm border-2 focus:border-[#b51a00]"
                        required={contactType === "vendor"}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="city" className="text-sm font-bold uppercase tracking-wider text-[#4a4a4a]">
                        City
                      </Label>
                      <Input
                        id="city"
                        name="city"
                        data-testid="contact-city"
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="e.g., Fort Worth, Denton"
                        className="mt-2 rounded-sm border-2 focus:border-[#b51a00]"
                      />
                    </div>
                    {contactType === "client" && (
                      <div>
                        <Label htmlFor="service" className="text-sm font-bold uppercase tracking-wider text-[#4a4a4a]">
                          Service Needed *
                        </Label>
                        <Select 
                          value={formData.service} 
                          onValueChange={(value) => handleSelectChange("service", value)}
                        >
                          <SelectTrigger 
                            data-testid="contact-service"
                            className="mt-2 rounded-sm border-2"
                          >
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent>
                            {services.map((service) => (
                              <SelectItem key={service} value={service}>
                                {service}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  {/* Client-specific Fields */}
                  {contactType === "client" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="contact_method" className="text-sm font-bold uppercase tracking-wider text-[#4a4a4a]">
                          Best Way to Contact You
                        </Label>
                        <Select 
                          value={formData.contact_method} 
                          onValueChange={(value) => handleSelectChange("contact_method", value)}
                        >
                          <SelectTrigger 
                            data-testid="contact-method"
                            className="mt-2 rounded-sm border-2"
                          >
                            <SelectValue placeholder="Select preference" />
                          </SelectTrigger>
                          <SelectContent>
                            {contactMethods.map((method) => (
                              <SelectItem key={method} value={method}>
                                {method}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="hear_about_us" className="text-sm font-bold uppercase tracking-wider text-[#4a4a4a]">
                          How Did You Hear About Us?
                        </Label>
                        <Select 
                          value={formData.hear_about_us} 
                          onValueChange={(value) => handleSelectChange("hear_about_us", value)}
                        >
                          <SelectTrigger 
                            data-testid="contact-hear-about"
                            className="mt-2 rounded-sm border-2"
                          >
                            <SelectValue placeholder="Select option" />
                          </SelectTrigger>
                          <SelectContent>
                            {hearAboutUs.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="message" className="text-sm font-bold uppercase tracking-wider text-[#4a4a4a]">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="contact-message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={contactType === "vendor" ? "Tell us about your business inquiry..." : "Tell us about your project..."}
                      rows={4}
                      className="mt-2 rounded-sm border-2 focus:border-[#b51a00] resize-none"
                    />
                  </div>

                  {/* SMS Consent Checkbox */}
                  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-sm border border-gray-200">
                    <Checkbox
                      id="sms_consent"
                      data-testid="sms-consent"
                      checked={formData.sms_consent}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, sms_consent: checked }))}
                      className="mt-1"
                    />
                    <div>
                      <Label htmlFor="sms_consent" className="text-sm text-gray-700 cursor-pointer leading-relaxed">
                        By checking this box, I consent to receive automated SMS/MMS text messages from Overhaul Ops LLC 
                        at the phone number provided above. Consent is not a condition of purchase. Message frequency varies. 
                        Message & data rates may apply. Reply STOP to unsubscribe or HELP for help.
                      </Label>
                      <p className="text-xs text-gray-500 mt-2">
                        View our <Link to="/sms-policy" className="text-[#b51a00] hover:underline font-medium">SMS Terms & Conditions</Link> and{" "}
                        <Link to="/privacy-policy" className="text-[#b51a00] hover:underline font-medium">Privacy Policy</Link>.
                      </p>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={loading}
                    className="w-full md:w-auto bg-[#b51a00] hover:bg-[#8a1300] text-white font-bold uppercase tracking-wider rounded-sm px-8 py-4 text-lg"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>

            {/* Contact Info Sidebar */}
            <div className="space-y-8">
              {/* Direct Contact */}
              <div className="bg-white p-8 rounded-sm shadow-sm">
                <h3 
                  className="text-xl font-bold uppercase tracking-tight mb-6 text-[#0f0f0f]"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  Direct Contact
                </h3>
                <div className="space-y-4">
                  <a 
                    href={phone_link} 
                    data-testid="contact-phone-link"
                    className="flex items-center gap-4 p-4 bg-[#f4f4f5] rounded-sm hover:bg-[#e5e5e5] transition-colors"
                  >
                    <div className="w-12 h-12 bg-[#b51a00] rounded-sm flex items-center justify-center">
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-[#707070] uppercase tracking-wider">Phone</p>
                      <p className="font-bold text-[#0f0f0f]">{formatted_phone}</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* Business Hours */}
              <div className="bg-white p-8 rounded-sm shadow-sm">
                <h3 
                  className="text-xl font-bold uppercase tracking-tight mb-6 text-[#0f0f0f]"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  Business Hours
                </h3>
                <div className="space-y-2">
                  {businessHours.map((item) => (
                    <div 
                      key={item.day}
                      className="flex justify-between py-2 border-b border-gray-100 last:border-0"
                    >
                      <span className="text-[#4a4a4a]">{item.day}</span>
                      <span className="font-medium text-[#0f0f0f]">{item.hours}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Service Areas */}
              <div className="bg-[#1a1a1a] p-8 rounded-sm text-white noise-texture">
                <h3 
                  className="text-xl font-bold uppercase tracking-tight mb-6"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  Service Areas
                </h3>
                <div className="space-y-3">
                  {serviceAreas.map((area) => (
                    <Link 
                      key={area.name}
                      to={`/locations/${area.slug}`}
                      className="flex items-center gap-3 hover:text-[#b51a00] transition-colors group"
                    >
                      <MapPin className="w-4 h-4 text-[#b51a00]" />
                      <span className="group-hover:underline">{area.name}</span>
                    </Link>
                  ))}
                </div>
                <Link 
                  to="/locations"
                  className="mt-4 inline-block text-sm text-[#b51a00] hover:underline"
                >
                  View All Service Areas →
                </Link>
                <div className="mt-6 pt-6 border-t border-white/20 flex items-center gap-3">
                  <Shield className="w-5 h-5 text-[#FFD700]" />
                  <span className="text-sm">Fully Insured • Firefighter Owned</span>
                </div>
              </div>

              {/* Interactive Clickable Service Area Map */}
              <div className="bg-white p-4 rounded-sm shadow-sm">
                <h3 
                  className="text-xl font-bold uppercase tracking-tight mb-4 text-[#0f0f0f]"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  Click a County
                </h3>
                <div className="relative rounded-sm overflow-hidden" style={{ aspectRatio: "4/3" }}>
                  {/* Background Map */}
                  <iframe
                    title="Overhaul Ops Service Area"
                    width="100%"
                    height="100%"
                    style={{ border: 0, position: "absolute", top: 0, left: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d429613.0!2d-97.05!3d32.85!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sus!4v1708985600000"
                  />
                  {/* Clickable Overlay Regions */}
                  {mapRegions.map((region) => (
                    <Link
                      key={region.name}
                      to={`/locations/${region.slug}`}
                      className="absolute flex items-center justify-center transition-all duration-300 hover:scale-105 group cursor-pointer"
                      style={{
                        top: region.position.top,
                        left: region.position.left,
                        width: region.position.width,
                        height: region.position.height,
                        backgroundColor: `${region.color}CC`,
                        borderRadius: "8px",
                        border: "2px solid white",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.3)"
                      }}
                      data-testid={`map-region-${region.slug}`}
                    >
                      <div className="text-center text-white p-2">
                        <span 
                          className="font-bold text-xs sm:text-sm block leading-tight"
                          style={{ fontFamily: 'Barlow Condensed, sans-serif', textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}
                        >
                          {region.name}
                        </span>
                        <span className="text-[10px] opacity-90 hidden sm:block mt-1">
                          {region.cities.slice(0, 2).join(" • ")}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-3 text-center">
                  <MapPin className="inline w-3 h-3 mr-1" />
                  Click any county for local service info & pricing
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
